import React, { useContext } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import styled, { ThemeContext } from "styled-components"
import { compose, layout } from "styled-system"
import { hexToRGBA } from "../../lib/color-helpers"

import Image from "../utils/Image"
import Link from "../utils/Link"
import SocialLink from "../utils/SocialLink"

import Box from "../atoms/Box"
import Card from "../atoms/Card"
import Flex from "../atoms/Flex"
import { Title, Paragraph } from "../atoms/Typography"
import ProportionalBox from "../atoms/ProportionalBox"

const DEFAULT_SOCIAL_LINK_STYLES = {
  color: "primary",
  theme: "highlight",
  bg: 3,
  iconStyle: "solid",
}

const NameText = ({ name, positionTitle, textClassName }) => {
  return (
    <Box height="fit-content" style={{ pointerEvents: "none" }}>
      <Title className={textClassName} variant="h3" mb={2}>
        {name}
      </Title>
      {positionTitle && (
        <Paragraph className={textClassName}>{positionTitle}</Paragraph>
      )}
    </Box>
  )
}

const NameBox = ({
  name,
  positionTitle,
  textClassName,
  center,
  align,
  borderRadius,
  textPlacement,
  children,
  ...rest
}) => {
  const computedTextAlign =
    rest.textAlign || (center ? "center" : align === "right" ? "right" : "left")

  // If children are provided, NameBox becomes a wrapper and should not render NameText (to prevent duplicate text)
  const hasChildren = React.Children.count(children) > 0

  return (
    <Box
      display={center || textPlacement === "onImage" ? "flex" : "block"}
      flexDirection="column"
      justifyContent={textPlacement === "onImage" ? "flex-end" : "center"}
      width="100%"
      textAlign={computedTextAlign}
      className="name-box"
      borderTopLeftRadius={"0px"} // Prevent unwanted border radius at the intersection of the image and text
      borderTopRightRadius={"0px"}
      borderBottomLeftRadius={borderRadius}
      borderBottomRightRadius={borderRadius}
      style={{ pointerEvents: "none" }}
      {...rest}
    >
      {hasChildren ? (
        children
      ) : (
        <NameText
          name={name}
          positionTitle={positionTitle}
          textClassName={textClassName}
        />
      )}
    </Box>
  )
}

/**
 * SocialLinksBox
 * - Uses flex gap for consistent spacing when wrapping
 * - Applies a smaller row gap only when iconStyle === "none"
 */
const SocialLinksBox = ({
  socialLinks,
  socialLinkStyles,
  socialLinkSize,
  socialLinkSpacing,
  textAlign = "left",
  mt = 3,
  width = "100%",
  wrap = false,
}) => {
  if (!Array.isArray(socialLinks) || socialLinks.length === 0) return null

  const justifyContent =
    textAlign === "center"
      ? "center"
      : textAlign === "right"
        ? "flex-end"
        : "flex-start"

  const colGapPx = typeof socialLinkSpacing === "number" ? socialLinkSpacing : 6

  const scale = typeof socialLinkSize === "number" ? socialLinkSize / 100 : 1
  const rowGapPx = Math.max(8, Math.round(10 * scale))

  const isNone = socialLinkStyles?.iconStyle === "none"

  // Reduce vertical spacing for wrapped rows when icons are rendered without a bubble (outline /solid style)
  const ROW_GAP_NONE_DIV = 1.2
  const rowGapAdjPx = isNone
    ? Math.max(0, Math.round(rowGapPx / ROW_GAP_NONE_DIV))
    : rowGapPx

  return (
    <Flex
      justifyContent={justifyContent}
      alignItems="center"
      mt={mt}
      width={width}
      flexWrap={wrap ? "wrap" : "nowrap"}
      style={{
        pointerEvents: "auto",
        ...(wrap ? { gap: `${rowGapAdjPx}px ${colGapPx}px` } : {}),
      }}
    >
      {socialLinks.map((socialLink, i) => (
        <SocialLink
          key={`${socialLink?.iconName || "social"}-${i}`}
          {...socialLink}
          {...socialLinkStyles}
          iconStyle={socialLinkStyles?.iconStyle}
          iconSize={socialLinkSize}
          socialSpacing={wrap ? 0 : colGapPx}
          index={i}
          noLink
        />
      ))}
    </Flex>
  )
}

const StyledCard = styled(Card)`
  position: relative;

  .name-box {
    opacity: ${props => (props.$showNameOnHover ? 0 : 1)};
    transition-property: opacity;
  }

  .image-container {
    .image {
      transition: transform 0.3s ease;
      will-change: transform;
    }

    /* Hover the container so overlay content doesn't block zoom hover */
    &:hover {
      .image {
        ${props => (props.zoomImageOnHover ? "transform: scale(1.05)" : null)};
      }
    }
  }

  &:hover {
    .name-box {
      ${props => (props.$showNameOnHover ? "opacity: 1" : null)};
    }
  }
`

const StyledOverlayImage = styled(Image)`
  opacity: 0;
  /* transition: opacity 0.3s cubic-bezier(0.34, 0.61, 0.48, 0.94); */
  transition: opacity 0.3s ease;
  will-change: opacity;

  &:hover {
    opacity: 1;
  }
`

const CircleLink = styled(Link)`
  .name {
    transition: opacity 0.3s ease;
    will-change: opacity;
  }
  &:hover .name {
    opacity: 0.6;
  }
  & .image img {
    transition: transform 0.3s ease;
    will-change: transform;
  }
  &:hover .image img {
    transform: scale(1.08);
  }
`

const CardLink = styled(Link)`
  .name-box {
    transition: opacity 0.4s ease;
    will-change: opacity;
  }
  & .name-box:hover {
    opacity: 0.6;
  }
  .image {
    transition: transform 0.4s ease;
    will-change: transform;
  }

  &:hover .image {
    ${props => (props.zoomImageOnHover ? "transform: scale(1.05)" : null)};
  }
`

const StyledImage = styled(Image)`
  ${compose(layout)}
`

const TeamCard = ({
  name,
  positionTitle,
  image,
  overlayImage,
  useCard,
  card,
  imageShape,
  link,
  socialLinks,
  socialLinkStyles,
  socialLinkSize,
  socialLinkSpacing,
  socialLinksLocation = "below",
  socialLinksAlign = "middle",
  showNameOnHover,
  textPlacement,
  overlayStyle,
  overlayOpacity,
  align,
  imageWidth = "full",
  imageRatio,
  zoomImageOnHover,
  imageFieldId,
}) => {
  const circle = imageShape === "circle"
  const center = align === "center"
  const right = align === "right"
  const showNameOnHoverOverride = !!textPlacement ? false : showNameOnHover // once textPlacement is set, ignore outdated showNameOnHover field. otherwise use it as is.
  const textOnImage = textPlacement === "onImage" || showNameOnHoverOverride

  const overrideProps = {}
  if (showNameOnHoverOverride) {
    // keep old padding numbers when showNameOnHover is still being used
    overrideProps.px = 4
    overrideProps.pb = 4
    overrideProps.pt = 3
  }

  const socialStylesObj =
    socialLinkStyles === undefined || socialLinkStyles === null
      ? DEFAULT_SOCIAL_LINK_STYLES
      : { ...DEFAULT_SOCIAL_LINK_STYLES, ...socialLinkStyles }

  const currentTheme = useContext(ThemeContext)
  const bgColor =
    currentTheme?.themes[card?.theme]?.colors?.background[card?.bg] || "#FFFFFF"
  const bgWithOpacity = hexToRGBA(
    bgColor,
    showNameOnHoverOverride ? 100 : overlayOpacity * 100,
  )
  const bgWithGradient = `linear-gradient(to top, ${bgWithOpacity} 0%, transparent 50%, transparent 100%)`

  const getImageRatio = imageRatio => {
    switch (imageRatio) {
      case "sixteenNine":
        return 0.5625
      case "threeTwo":
        return 0.66
      case "fourThree":
        return 0.75
      case "oneOne":
        return 1
      case "threeFour":
        return 1.33
      case "twoThree":
        return 1.5
      default:
        return 0.75
    }
  }

  // text alignment for non-circle layouts
  const textAlignVal = center ? "center" : right ? "right" : "left"

  const getInlineAlignItems = value => {
    switch (value) {
      case "top":
        return "flex-start"
      case "bottom":
        return "flex-end"
      case "middle":
      default:
        return "center"
    }
  }

  const renderNameAndSocial = ({ textAlign, location }) => {
    const hasSocial = Array.isArray(socialLinks) && socialLinks.length > 0
    const wrapIcons = hasSocial && socialLinks.length >= 4

    // Inline rules:
    // if left text => icons right (desktop)
    // if right text => icons left (desktop)
    // if center text => icons below (centered)
    //
    // When there are 4+ icons, stack icons below for inline
    const isInline = location === "inline" && hasSocial
    const shouldForceStack = isInline && (textAlign === "center" || wrapIcons)
    const isSideInline = isInline && textAlign !== "center" && !shouldForceStack

    if (isSideInline) {
      const rowReverse = textAlign === "right"
      return (
        <Flex
          width="100%"
          justifyContent="space-between"
          alignItems={getInlineAlignItems(socialLinksAlign)}
          flexDirection={rowReverse ? "row-reverse" : "row"}
        >
          <Box flex="1 1 auto" minWidth={0}>
            <NameText
              name={name}
              positionTitle={positionTitle}
              textClassName="name"
            />
          </Box>

          <Box flex="0 0 auto" ml={!rowReverse ? 3 : 0} mr={rowReverse ? 3 : 0}>
            <SocialLinksBox
              socialLinks={socialLinks}
              socialLinkStyles={socialStylesObj}
              socialLinkSize={socialLinkSize}
              socialLinkSpacing={socialLinkSpacing}
              textAlign={textAlign}
              mt={0}
              width="auto"
              wrap={false}
            />
          </Box>
        </Flex>
      )
    }

    // Default: stack name and icons below (also used for center-inline and forced-stack)
    return (
      <>
        <NameText
          name={name}
          positionTitle={positionTitle}
          textClassName="name"
        />
        <SocialLinksBox
          socialLinks={socialLinks}
          socialLinkStyles={socialStylesObj}
          socialLinkSize={socialLinkSize}
          socialLinkSpacing={socialLinkSpacing}
          textAlign={textAlign}
          mt={hasSocial ? (wrapIcons ? 4 : 3) : 0}
          width="100%"
          wrap={wrapIcons}
        />
      </>
    )
  }

  if (circle) {
    return (
      <CircleLink optional {...link} zoomImageOnHover={false} width="100%">
        <Card boxShadow="none" bg="transparent" overflow="visible">
          <Flex
            flexDirection={center ? "column" : "row"}
            alignItems="center"
            position="relative"
          >
            {image && (
              <Box flex="0 0 auto" mb={center ? 4 : 0} height={6} width={6}>
                <Image
                  className="image"
                  alternativeText="Thumbnail Image Of A Team Member"
                  {...image}
                  style={{
                    height: "100%",
                    borderRadius: "50%",
                    willChange: "transform",
                    margin: "0.5px",
                  }}
                  imgStyle={{
                    borderRadius: "50%",
                  }}
                  imageFieldId={imageFieldId}
                />
              </Box>
            )}
            {overlayImage && (
              <Box height={6} width={6} position="absolute">
                <StyledOverlayImage
                  className="image"
                  {...overlayImage}
                  style={{
                    height: "100%",
                    borderRadius: "50%",
                    margin: "0.5px",
                    zIndex: 100,
                  }}
                  imgStyle={{
                    borderRadius: "50%",
                  }}
                />
              </Box>
            )}

            <Box flex="1 1 auto" width="100%" ml={!center && image ? 4 : 0}>
              <Box
                width="100%"
                textAlign={center ? "center" : right ? "right" : "left"}
              >
                {renderNameAndSocial({
                  textAlign: center ? "center" : right ? "right" : "left",
                  location: socialLinksLocation,
                })}
              </Box>
            </Box>
          </Flex>
        </Card>
      </CircleLink>
    )
  }

  return (
    <CardLink
      optional
      {...link}
      zoomImageOnHover={zoomImageOnHover}
      width="100%"
    >
      <StyledCard
        height="100%"
        width="100%"
        $showNameOnHover={showNameOnHoverOverride}
        zoomImageOnHover={zoomImageOnHover}
        {...card}
        useCard={useCard}
        bg={null}
      >
        {(image || overlayImage || textOnImage) && (
          <ProportionalBox
            display="flex"
            flex={1.8}
            ratio={getImageRatio(imageRatio)}
            className="image-container"
            transitionProperty="height, transform"
            transitionTimingFunction="ease"
            transitionDuration="fast"
            overflow="hidden"
          >
            {textOnImage && (
              <NameBox
                zIndex={200}
                textAlign={textAlignVal}
                textClassName="name"
                name={name}
                positionTitle={positionTitle}
                position="absolute"
                height={
                  showNameOnHoverOverride || textPlacement === "belowImage"
                    ? "fit-content"
                    : "100%"
                }
                width="100%"
                bottom="0"
                p="20px"
                pb="24px"
                transitionDuration="fast"
                transitionTimingFunction="ease"
                bg={
                  useCard === false || overlayStyle === "gradient"
                    ? null
                    : bgWithOpacity
                }
                backgroundImage={
                  useCard === true && overlayStyle === "gradient"
                    ? bgWithGradient
                    : null
                }
                borderRadius="inherit"
                textPlacement={textPlacement}
                {...overrideProps}
              >
                {renderNameAndSocial({
                  textAlign: textAlignVal,
                  location: socialLinksLocation,
                })}
              </NameBox>
            )}

            {(image || overlayImage) && (
              <Box
                className="image"
                height="100%"
                transitionProperty="height, transform"
                transitionTimingFunction="ease"
                transitionDuration="md"
                overflow="hidden"
              >
                {image && (
                  <StyledImage
                    className="image"
                    {...image}
                    style={{
                      height: "100%",
                      width: "100%",
                    }}
                    imgStyle={{
                      padding: imageWidth === "full" ? "0px" : "24px",
                    }}
                    minHeight={!useCard ? "100%" : "auto"}
                    imageFieldId={imageFieldId}
                  />
                )}
                {overlayImage && (
                  <StyledOverlayImage
                    className="image"
                    {...overlayImage}
                    style={{
                      height: "100%",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      position: "absolute",
                      zIndex: 100,
                    }}
                    imgStyle={{
                      padding: imageWidth === "full" ? "0px" : "24px",
                    }}
                  />
                )}
              </Box>
            )}
          </ProportionalBox>
        )}

        {!textOnImage && (
          <NameBox
            textAlign={textAlignVal}
            textClassName="name"
            name={name}
            positionTitle={positionTitle}
            position="relative"
            height="100%"
            width="100%"
            bottom="0"
            padding={useCard === true ? 4 : 0}
            pt={imageWidth === "full" ? 3 : 1}
            transitionDuration="fast"
            transitionTimingFunction="ease"
            bg={useCard === true ? bgColor : null}
            borderRadius="inherit"
            textPlacement={textPlacement}
          >
            {renderNameAndSocial({
              textAlign: textAlignVal,
              location: socialLinksLocation,
            })}
          </NameBox>
        )}
      </StyledCard>
    </CardLink>
  )
}

export default TeamCard

TeamCard.strapiProps = {
  name: PropTypes.string.isRequired,
  positionTitle: PropTypes.string,
  image: PropTypes.object,
  tags: PropTypes.arrayOf(PropTypes.object),
  socialLinks: PropTypes.arrayOf(PropTypes.object),
}

TeamCard.propTypes = {
  ...TeamCard.strapiProps,
  imageShape: PropTypes.oneOf(["default", "square", "circle"]),
  showNameOnHover: PropTypes.bool,
  zoomImageOnHover: PropTypes.bool,
  card: PropTypes.shape(Card.strapiProps),
  link: PropTypes.shape(Link.strapiProps),
  align: PropTypes.oneOf(["left", "center", "right"]),
  socialLinkStyles: PropTypes.object,
  socialLinksLocation: PropTypes.oneOf(["inline", "below"]),
  socialLinksAlign: PropTypes.oneOf(["top", "middle", "bottom"]),
  socialLinkSize: PropTypes.number,
  socialLinkSpacing: PropTypes.number,
}

export const query = graphql`
  fragment TeamCard on STRAPI__COMPONENT_MOLECULES_TEAM_CARD {
    name
    positionTitle
    image {
      ...ImageWithConstrainedLayout
    }
    overlayImage {
      ...ImageWithConstrainedLayout
    }
    link {
      ...Link
    }
    tags {
      name
    }
    socialLinks {
      ...SocialLink
    }
  }
`
